import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "@/styles/bottom-navbar.scss";
import { isSessionValid } from "@/utils/auth";
import circleNotchIcon from "@/components/icons/circle-notch-icon.jsx";
import forwardStepIcon from "@/components/icons/forward-step-icon.jsx";
import homeIcon from "@/components/icons/home-icon.jsx";
import horseIcon from "@/components/icons/horse-icon.jsx";
import lexiIcon from "@/components/icons/lexi-icon.jsx";
import lexiTipsIcon from "@/components/icons/lexi-tips-icon.jsx";
import lexiTipsIconHorizontal from "@/components/icons/lexi-tips-icon-horiz.jsx";
import listIcon from "@/components/icons/list-icon.jsx";
import LockIcon from "@/components/icons/lock-icon.jsx";
import resultsIcon from "@/components/icons/results-icon.jsx";
import clockIcon from "@/components/icons/clock-icon.jsx";
import GaugeIcon from "./icons/gauge-icon";
import logger from "@/utils/logger";

// Update to accept and pass carouselRef
export default function BottomNavbar({ onNextToJump, carouselRef }) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");
  const [isListView, setIsListView] = useState(true);
  const [showListIcon, setShowListIcon] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isSessionValid();
      setIsAuthenticated(authenticated);
    };

    // Check immediately
    checkAuth();

    // Set up interval to check periodically
    const interval = setInterval(checkAuth, 10000);

    // Check when window becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Reset view mode when navigating away from home
  useEffect(() => {
    if (path !== "/home") {
      setShowListIcon(true);
    } else {
      // When on home page, start with circle notch icon
      setShowListIcon(false);
    }
  }, [path]);

  useEffect(() => {
    if (path !== "/home") {
      setIsListView(true);
    } else {
      // Default to list view if no viewMode is specified
      const viewMode = searchParams.get("viewMode") || "list";
      setIsListView(viewMode === "list");
    }
  }, [path, location.search]);

  const isLinkActive = (linkPath, linkType = null) => {
    // Special case for Next to Jump button
    if (linkPath === "#") {
      return true;
    }
    return linkType
      ? path === linkPath && type === linkType
      : path === linkPath || path.startsWith(linkPath + "/");
  };

  // Function to refresh the current page
  const refreshCurrentPage = () => {
    navigate(0);
  };

  // Handle click on protected route when not authenticated
  const handleProtectedRouteClick = (e, route) => {
    if (!isAuthenticated) {
      e.preventDefault();
      // Redirect to login page
      navigate("/login", {
        state: {
          from: route,
          message: "You need to log in to access this page",
        },
      });
      return false;
    }
    return true;
  };

  // Use the passed onNextToJump function if provided, otherwise use default
  const handleNextToJump = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          from: "/todays-tips",
          message: "You need to log in to access this feature",
        },
      });
      return;
    }

    if (onNextToJump) {
      onNextToJump();
    } else {
      navigate("/home?nextToJump=true");
    }
  };

  // Function to navigate to today's tips with appropriate parameters
  const navigateToTodaysTips = (e) => {
    e.preventDefault();

    // Check if user is authenticated first
    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          from: "/todays-tips",
          message: "You need to log in to access Lexi's Tips",
        },
      });
      return;
    }

    // Check if we're already on today's tips page - if so, refresh
    if (path === "/todays-tips") {
      refreshCurrentPage();
      return;
    }

    // Check if we have access to the carousel reference
    if (
      carouselRef &&
      carouselRef.current &&
      typeof carouselRef.current._getNextAvailableRace === "function"
    ) {
      // Get the next race using the carousel's method
      const nextRace = carouselRef.current._getNextAvailableRace();

      let redirectPath = "/todays-tips";

      if (nextRace) {
        // Format track name for URL
        const formattedTrackName = nextRace.track
          .replace(/\s+/g, "-")
          .toLowerCase();
        const formattedRaceNumber = nextRace.raceNumber.toString();

        // Create path for React Router navigation
        const todaysTipsPath = `/todays-tips?track=${formattedTrackName}&race=${formattedRaceNumber}`;

        // Use React Router's navigate function (prevents page reload)
        navigate(todaysTipsPath);

        // Dispatch custom event to notify components about the URL change
        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: {
              pathname: "/todays-tips",
              search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
              track: formattedTrackName,
              race: formattedRaceNumber,
            },
          }),
        );
      } else {
        // No race found, just navigate to the base URL
        navigate("/todays-tips");
      }

      // Use navigate from React Router to perform client-side navigation
      navigate(redirectPath);
    } else {
      // If no carousel ref is available or it doesn't have the method,
      // navigate to the base route - the useEffect in home-page.jsx will handle adding the parameters
      logger.log("No carousel ref available, navigating to base route");
      navigate("/todays-tips");
    }
  };

  const navigationItems = [
    {
      name: "Schedule",
      href: "/home",
      icon: horseIcon,
      protected: false,
    },
    {
      name: "My Tips",
      href: "/my-tips",
      icon: listIcon,
      protected: true,
    },
    {
      name: "LEXI.TIPS",
      href: "/todays-tips",
      icon: homeIcon,
      icon2: lexiTipsIconHorizontal,
      onClick: navigateToTodaysTips,
      protected: true,
      default: true
    },
    {
      name: "Results",
      href: "/results",
      icon: resultsIcon,
      protected: true,
    },
    {
      name: "Dashboard",
      href: "/app/dashboard",
      icon: GaugeIcon,
      protected: true,
    },
  ];

  return (
    <div className="bottom-navbar max-w-md mx-auto fixed h-[60px] bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-0 z-50 font-roboto">
      <div className="bottom-navbar-container flex justify-around items-center relative">
        {navigationItems.map((item, index) => {
          const active = isLinkActive(item.href, type);
          const isTodaysTips = item.default;
          const needsAuth = item.protected && !isAuthenticated;

          if (item.onClick) {
            return (
              <button
                key={item.name}
                onClick={item.onClick}
                className={`nav-item flex flex-1 flex-col items-center justify-center py-2 bg-white  
                  ${isTodaysTips ? "isTodaysTips relative -mt-1 w-[26%] flex-none !bg-transparent " : ""} 
                  ${active ? "active !bg-[#f30099]" : ""}
                  ${needsAuth ? "opacity-70" : ""}
                `}
                data-gtm-element-id={`footer_my-tips_link-${index}`}
              >
                {isTodaysTips ? (
                  <>
                    <span
                      className={`absolute p-3 rounded-full shadow-md w-[90px] h-[90px] top-0 left-1/2 transform -translate-x-1/2 -translate-y-[13px] scale-[1.05] ${
                        active
                          ? "bg-[#f30099]"
                          : "bg-white border border-gray-200"
                      }`}
                    ></span>
                    <item.icon
                      fill={active ? "#FFFFFF" : "#9E9B9B"}
                      className="w-8 h-8 z-[30]  -translate-y-[5px] scale-[1.2]"
                    />
                    {item.icon2 && (
                      <item.icon2
                        fill={active ? "#FFFFFF" : "#f30099"}
                        fill2={active ? "#FFFFFF" : "#000000"}
                        className="w-8 h-8 z-[30] scale-[1.7] transform -translate-y-[-2px]"
                      />
                    )}
                  </>
                ) : (
                  <div className="relative">
                    {needsAuth ? (
                      <LockIcon className="opacity-70 w-6 h-6 text-[#f30099] transform -translate-y-[2px] scale-[0.9]" />
                    ) : (
                      <item.icon
                        fill={active ? "#FFFFFF" : "#f30099"}
                        fill2={active ? "#FFFFFF" : "#000000"}
                        className={`w-6 h-6 ${index === 0 ? "scale-[120%]" : ""}`}
                      />
                    )}
                  </div>
                )}
                {isTodaysTips ? (
                   null 
                ) : (
                  <span
                    className={`font-bold text-xxs mt-1 
                    ${active ? (isTodaysTips ? "text-[#ffffff] z-[30]" : "text-[#ffffff] ") : isTodaysTips ? "text-[#9E9B9B] z-[30]" : "text-[#9E9B9B]"}
                    `}
                  >
                    {item.name}
                  </span>
                )}
              </button>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => {
                if (needsAuth) {
                  handleProtectedRouteClick(e, item.href);
                }
                if (active) {
                  e.preventDefault();
                  refreshCurrentPage();
                  return;
                }
              }}
              className={`nav-item flex flex-1 flex-col items-center justify-center py-2 
                ${active ? " active bg-[#f30099] " : "bg-white"}
                ${isTodaysTips ? " isTodaysTips relative w-[26%] flex-none !bg-transparent " : ""} 
                ${needsAuth ? "opacity-70" : ""}
                `}
              data-gtm-element-id={`footer_my-tips_link-${index}`}
            >
              {isTodaysTips ? (
                <>
                  <span
                    className={`absolute p-3 rounded-full shadow-md w-[90px] h-[90px] top-0 left-1/2 transform -translate-x-1/2 -translate-y-[13px] scale-[1.05] ${
                      active
                        ? "bg-[#f30099] active"
                        : "bg-white border border-gray-200"
                    }`}
                  ></span>
                  <item.icon
                    fill={active ? "#FFFFFF" : "#9E9B9B"}
                    className="w-7 h-7 z-[30] transform -translate-y-[5px] scale-[1.2]"
                  />
                </>
              ) : (
                <div className="relative">
                  {needsAuth ? (
                    <LockIcon className="opacity-70 w-6 h-6 text-[#f30099] transform -translate-y-[2px] scale-[0.9]" />
                  ) : (
                    <item.icon
                      fill={active ? "#FFFFFF" : "#9E9B9B"}
                      className={`w-6 h-6 ${index === 0 ? "scale-[120%]" : ""}`}
                    />
                  )}
                </div>
              )}
              <span
                className={`font-bold text-xxs mt-1 
                  ${active ? (isTodaysTips ? "text-[#ffffff] z-[30] isTodaysTips active" : "text-[#ffffff] active ") : isTodaysTips ? "text-[#9E9B9B] z-[30] isTodaysTips" : "text-[#9E9B9B]"}
                   `}
              >
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
